import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Plus, TrendingUp, Building, AlertCircle, ChevronRight } from 'lucide-react';
import { CardStats, Card, StatusBadge, ExportDropdown, Pagination, SearchInput } from '@components/ui';
import { getSalesByConstructionId, getSalesStats } from '@mocks/satislar';
import { useExportExcel } from '@hooks';
import { toast } from 'react-toastify';
import SatisEkleModal from './components/SatisEkleModal';
import clsx from 'clsx';

export default function Sales() {
    const navigate = useNavigate();
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [searchQuery, setSearchQuery] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const allSales = getSalesByConstructionId(constructionId);

    // Arama filtrelemesi
    const sales = allSales.filter(sale => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            sale.buyer.toLowerCase().includes(query) ||
            sale.apartmentNo.toLowerCase().includes(query) ||
            sale.blockName.toLowerCase().includes(query)
        );
    });

    const stats = getSalesStats(allSales);

    // Paginated Sales
    const paginatedSales = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return sales.slice(startIndex, startIndex + pageSize);
    }, [sales, currentPage, pageSize]);

    // Arama değiştiğinde ilk sayfaya dön
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // Export
    const { exportToExcel, isExporting } = useExportExcel();

    const handleExportExcel = () => {
        exportToExcel({
            data: sales.map(s => ({
                'Daire': s.apartmentNumber,
                'Blok': s.block,
                'Müşteri': s.customerName,
                'Satış Fiyatı': s.salePrice,
                'Ödenen': s.paidAmount,
                'Kalan': s.remainingAmount,
                'Satış Tarihi': s.saleDate,
                'Durum': s.paymentStatus === 'completed' ? 'Tamamlandı' : s.paymentStatus === 'pending' ? 'Bekliyor' : 'Gecikmiş',
            })),
            columns: [
                { header: 'Daire', key: 'Daire' },
                { header: 'Blok', key: 'Blok' },
                { header: 'Müşteri', key: 'Müşteri' },
                { header: 'Satış Fiyatı', key: 'Satış Fiyatı', format: 'currency' },
                { header: 'Ödenen', key: 'Ödenen', format: 'currency' },
                { header: 'Kalan', key: 'Kalan', format: 'currency' },
                { header: 'Satış Tarihi', key: 'Satış Tarihi' },
                { header: 'Durum', key: 'Durum' },
            ],
            fileName: 'satislar',
            sheetName: 'Satışlar',
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Satışlar</h1>
                    <p className="text-text-tertiary mt-1">
                        {showAllConstructions
                            ? 'Tüm inşaatlar - Daire satışları ve gelir takibi'
                            : `${selectedConstruction?.name || ''} - Daire satışları`}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <ExportDropdown
                        onExportExcel={handleExportExcel}
                        isExporting={isExporting}
                    />
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-success text-white rounded-lg hover:bg-success-dark transition-colors"
                    >
                        <Plus size={18} />
                        <span className="font-medium">Yeni Satış</span>
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <CardStats
                    title="Toplam Satış"
                    value={formatCurrency(stats.totalSales)}
                    icon={DollarSign}
                    color="success"
                    size="lg"
                />
                <CardStats
                    title="Tahsil Edilen"
                    value={formatCurrency(stats.totalPaid)}
                    icon={TrendingUp}
                    color="primary"
                    size="lg"
                />
                <CardStats
                    title="Bekleyen Alacak"
                    value={formatCurrency(stats.totalRemaining)}
                    icon={Building}
                    color="warning"
                    size="lg"
                />
                <CardStats
                    title="Satılan Daire"
                    value={stats.apartmentCount.toString()}
                    icon={Building}
                    color="tertiary"
                    size="lg"
                />
            </div>

            {/* Arama ve Filtreleme */}
            <div className="flex flex-col sm:flex-row gap-3">
                <SearchInput
                    value={searchQuery}
                    onChange={(val) => setSearchQuery(val)}
                    placeholder="Müşteri, daire veya blok ara..."
                    fullWidth
                    className="max-w-md"
                />
            </div>

            {/* Satış Tablosu */}
            <Card padding="none" className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-bg-tertiary border-b border-border-light">
                                <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">Daire</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary">Alıcı</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-text-primary hidden md:table-cell">Satış Tarihi</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-text-primary">Toplam</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-text-primary hidden lg:table-cell">Tahsil</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-text-primary">Kalan</th>
                                <th className="text-center py-3 px-4 text-sm font-semibold text-text-primary">Durum</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedSales.map((sale, idx) => (
                                <tr
                                    key={sale.id}
                                    className={clsx(
                                        "border-b border-border-light hover:bg-bg-hover transition-colors",
                                        idx % 2 === 0 ? "bg-bg-secondary" : "bg-bg-primary"
                                    )}
                                >
                                    <td className="py-3 px-4">
                                        <div>
                                            <p className="font-medium text-text-primary">{sale.apartmentNo}</p>
                                            <p className="text-xs text-text-muted">{sale.blockName}</p>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4">
                                        <button
                                            onClick={() => navigate(`/finans/musteriler/${sale.buyerId}`)}
                                            className="text-primary hover:underline font-medium cursor-pointer"
                                        >
                                            {sale.buyer}
                                        </button>
                                    </td>
                                    <td className="py-3 px-4 text-text-secondary hidden md:table-cell">
                                        {formatDate(sale.saleDate)}
                                    </td>
                                    <td className="py-3 px-4 text-right font-medium text-text-primary">
                                        {formatCurrency(sale.totalPrice)}
                                    </td>
                                    <td className="py-3 px-4 text-right text-success hidden lg:table-cell">
                                        {formatCurrency(sale.totalPaid)}
                                    </td>
                                    <td className="py-3 px-4 text-right font-medium text-warning">
                                        {formatCurrency(sale.totalRemaining)}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <StatusBadge
                                            status={sale.status === 'active' ? 'Aktif' : 'Gecikmiş'}
                                            variant={sale.status === 'active' ? 'success' : 'error'}
                                        />
                                    </td>
                                    <td className="py-3 px-4">
                                        <button className="p-2 hover:bg-bg-hover rounded-lg transition-colors text-text-tertiary">
                                            <ChevronRight size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {paginatedSales.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="py-8 text-center text-text-muted">
                                        Kayıt bulunamadı.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {sales.length > 0 && (
                    <div className="border-t border-border-light px-4">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(sales.length / pageSize)}
                            onPageChange={setCurrentPage}
                            totalItems={sales.length}
                            pageSize={pageSize}
                        />
                    </div>
                )}
            </Card>

            {/* Yeni Satış Modal */}
            <SatisEkleModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={(data) => {
                    console.log('Yeni Satış:', data);
                    toast.success(`${data.customerName} için Daire Satışı başarıyla kaydedildi.`);
                }}
            />
        </div>
    );
}
